/**
 * Circle Chat Page
 * 
 * Full-page view for accountability circle interactions including:
 * - Real-time chat with circle members
 * - Weekly check-in submission
 * - View past check-ins from circle members
 * - Circle member list
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Users, Send, Loader2, MessageCircle, Sparkles, 
  ArrowLeft, Target, Trophy, Calendar, CheckCircle,
  Heart, Flame, Star
} from 'lucide-react';
import { getProfileImageUrl } from '@/lib/utils';
import MentionTextarea, { MentionText } from '@/components/MentionTextarea';
import { useAuth } from '@/context/AuthContext';

const API_URL = window.location.origin;

const CELEBRATION_REACTIONS = ["❤️", "🎉", "🤝🏾", "💡", "🤔", "👏🏾", "💪🏾", "🙌🏾", "🙏🏾", "💎", "🔥", "✨"];

const CircleChatPage = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const { user } = useAuth();
  
  const [circle, setCircle] = useState(null);
  const [messages, setMessages] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [checkinPrompt, setCheckinPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [activeTab, setActiveTab] = useState('chat');
  const [showMsgReactionPicker, setShowMsgReactionPicker] = useState(null);
  
  // Check-in form state
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);
  const [checkinResponse, setCheckinResponse] = useState('');
  const [checkinWins, setCheckinWins] = useState('');
  const [checkinGoal, setCheckinGoal] = useState('');
  const [submittingCheckin, setSubmittingCheckin] = useState(false);
  const [circleMembers, setCircleMembers] = useState([]);

  useEffect(() => {
    fetchCircleData();
    fetchCheckinPrompt();
    
    // Poll for new messages every 10 seconds (will use circle from state)
    const interval = setInterval(() => {
      if (circle?.id) {
        fetchMessages(circle.id);
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [circleId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchCircleData = async () => {
    try {
      const token = localStorage.getItem('token');

      // If a circleId is in the URL, fetch THAT circle's details directly.
      // This avoids the long-standing bug where /mine returns the user's
      // "first" circle regardless of which one they're trying to view —
      // which made the page show the wrong member count + route check-ins
      // and messages to the wrong place.
      if (circleId) {
        const detailRes = await axios.get(
          `${API_URL}/api/accountability-circle/${circleId}/details`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const targetCircle = detailRes.data?.circle;
        if (targetCircle) {
          setCircle(targetCircle);
          setCircleMembers(targetCircle.members || []);
          await fetchMessages(targetCircle.id);
          await fetchCheckins(targetCircle.id);
          return;
        }
      }

      // Fallback: no URL circle id → load the user's own circle
      const response = await axios.get(`${API_URL}/api/accountability-circle/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.in_circle && response.data.circle) {
        const userCircle = response.data.circle;
        setCircle(userCircle);

        // Now fetch messages and checkins with the circle ID
        await fetchMessages(userCircle.id);
        await fetchCheckins(userCircle.id);

        // Fetch members for @mentions
        try {
          const membersRes = await axios.get(
            `${API_URL}/api/accountability-circle/${userCircle.id}/members`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCircleMembers(membersRes.data || []);
        } catch (e) {
          console.log('Could not fetch circle members for mentions');
        }
      } else {
        // User not in a circle, redirect to dashboard
        toast.error('You are not in an accountability circle');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch circle:', error);
      toast.error('Failed to load circle data');
    } finally {
      setLoading(false);
    }
  };

  // Mark circle as visited when user opens it
  const markCircleVisited = async (cId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/accountability-circle/${cId}/mark-visited`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Silently fail - this is non-critical
      console.log('Could not mark circle visited:', error.message);
    }
  };

  const fetchMessages = async (userCircleId = null) => {
    const cId = userCircleId || circleId || circle?.id;
    if (!cId) return;
    
    // Mark as visited when fetching messages
    markCircleVisited(cId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/accountability-circle/${cId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchCheckins = async (userCircleId = null) => {
    const cId = userCircleId || circleId || circle?.id;
    if (!cId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/accountability-circle/${cId}/checkins`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCheckins(response.data.checkins || []);
    } catch (error) {
      console.error('Failed to fetch check-ins:', error);
    }
  };

  const fetchCheckinPrompt = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/accountability-circle/checkin/prompt`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCheckinPrompt(response.data);
    } catch (error) {
      console.error('Failed to fetch check-in prompt:', error);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim()) return;
    
    setSending(true);
    try {
      const cId = circleId || circle?.id;
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/accountability-circle/${cId}/messages`,
        { content: messageText, message_type: 'text' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessageText('');
      fetchMessages(cId);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleMsgReaction = async (messageId, emoji) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/accountability-circle/${circleId}/messages/${messageId}/react?reaction=${encodeURIComponent(emoji)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, reactions: res.data.reactions } : m
      ));
      setShowMsgReactionPicker(null);
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const groupReactions = (reactions) => {
    if (!reactions?.length) return {};
    const grouped = {};
    reactions.forEach(r => {
      if (!grouped[r.reaction]) grouped[r.reaction] = [];
      grouped[r.reaction].push(r);
    });
    return grouped;
  };



  const handleSubmitCheckin = async () => {
    if (!checkinResponse.trim()) {
      toast.error('Please write your check-in response');
      return;
    }
    
    setSubmittingCheckin(true);
    try {
      const token = localStorage.getItem('token');
      // Pass the circle_id from the URL so the check-in always lands in the
      // circle the user is actively viewing, not the random "first" circle
      // they happen to be a member of.
      const targetCircleId = circleId || circle?.id;
      const response = await axios.post(
        `${API_URL}/api/accountability-circle/checkin`,
        {
          circle_id: targetCircleId,
          response: checkinResponse,
          wins: checkinWins ? [checkinWins] : [],
          goal_for_week: checkinGoal
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Check-in submitted!');
      setShowCheckinDialog(false);
      setCheckinResponse('');
      setCheckinWins('');
      setCheckinGoal('');
      
      // Refresh data
      const cId = circleId || circle?.id;
      fetchCheckins(cId);
      fetchMessages(cId);
      fetchCheckinPrompt();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit check-in');
    } finally {
      setSubmittingCheckin(false);
    }
  };

  const reactToCheckin = async (checkinId, reaction) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accountability-circle/checkin/${checkinId}/react`,
        { reaction },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Optimistically update the UI
      setCheckins(prev => prev.map(c => {
        if (c.id === checkinId) {
          const existingReactions = c.reactions || [];
          return {
            ...c,
            reactions: [...existingReactions, { reaction, user_id: 'current', created_at: new Date().toISOString() }]
          };
        }
        return c;
      }));
      
      toast.success('💎 Encouragement sent!');
      
      // Refresh checkins in background to get accurate data
      const cId = circleId || circle?.id;
      setTimeout(() => fetchCheckins(cId), 1000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Failed to react';
      toast.error(errorMsg);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
        </div>
      </DashboardLayout>
    );
  }

  if (!circle) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-heading text-forest mb-2">No Circle Found</h2>
          <p className="text-gray-600 mb-4">You're not currently in an accountability circle.</p>
          <Link to="/dashboard">
            <Button className="bg-terracotta hover:bg-terracotta/90 text-white">
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="circle-chat-page">
        {/* Header */}
        <div className="mb-6">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-600 hover:text-forest mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading text-forest flex items-center gap-2">
                <Users className="h-6 w-6 text-purple-600" />
                {circle.name}
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                {circle.member_count} sisters supporting each other
              </p>
            </div>
            
            <Button
              onClick={() => setShowCheckinDialog(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="weekly-checkin-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Weekly Check-In
            </Button>
          </div>
          
          {/* Streak Badge */}
          {checkinPrompt && checkinPrompt.current_streak > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full">
              <Flame className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-amber-700">
                {checkinPrompt.current_streak} week streak!
              </span>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Chat & Check-ins Area */}
          <div className="lg:col-span-3">
            <Card className="card-base">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <CardHeader className="pb-2 border-b">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="chat">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Circle Chat
                    </TabsTrigger>
                    <TabsTrigger value="checkins">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Weekly Check-Ins
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>
                
                <CardContent className="p-0">
                  {/* Chat Tab */}
                  <TabsContent value="chat" className="m-0">
                    <ScrollArea className="h-[400px] p-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-12">
                          <MessageCircle className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-gray-500">No messages yet</p>
                          <p className="text-gray-400 text-sm">Be the first to say hello!</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map(message => (
                            <div key={message.id} className="flex gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={getProfileImageUrl(message.sender?.profile_image)} />
                                <AvatarFallback className="bg-purple-100 text-purple-600 text-xs">
                                  {message.sender?.full_name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm text-forest">
                                    {message.sender?.full_name?.split(' ')[0]}
                                  </span>
                                  {message.message_type === 'checkin' && (
                                    <Badge className="bg-purple-100 text-purple-700 text-xs">
                                      Check-In
                                    </Badge>
                                  )}
                                  <span className="text-xs text-gray-400">
                                    {new Date(message.created_at).toLocaleTimeString([], { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })}
                                  </span>
                                </div>
                                <div className={`p-3 rounded-lg ${
                                  message.message_type === 'checkin' 
                                    ? 'bg-purple-50 border border-purple-100' 
                                    : 'bg-gray-50'
                                }`}>
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                    <MentionText content={message.content} />
                                  </p>
                                </div>
                                {/* Reaction display & button */}
                                <div className="flex items-center gap-1 mt-1 ml-1 flex-wrap">
                                  {Object.entries(groupReactions(message.reactions)).map(([emoji, users]) => (
                                    <button
                                      key={emoji}
                                      onClick={() => handleMsgReaction(message.id, emoji)}
                                      className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full border transition-colors ${
                                        users.some(u => u.user_id === user?.id)
                                          ? 'bg-teal-50 border-teal-300'
                                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                      }`}
                                      title={users.map(u => u.user_name).join(', ')}
                                    >
                                      <span>{emoji}</span>
                                      {users.length > 1 && <span className="text-gray-500">{users.length}</span>}
                                    </button>
                                  ))}
                                  <div className="relative">
                                    <button
                                      onClick={() => setShowMsgReactionPicker(showMsgReactionPicker === message.id ? null : message.id)}
                                      className="text-xs text-gray-300 hover:text-gray-500 px-1 py-0.5 rounded transition-colors"
                                      data-testid={`msg-react-btn-${message.id}`}
                                    >
                                      +
                                    </button>
                                    {showMsgReactionPicker === message.id && (
                                      <div className="absolute bottom-full left-0 mb-1 flex flex-wrap gap-1 bg-white rounded-xl shadow-lg border px-2 py-1.5 z-50 w-[220px]">
                                        {CELEBRATION_REACTIONS.map(emoji => (
                                          <button
                                            key={emoji}
                                            onClick={() => handleMsgReaction(message.id, emoji)}
                                            className="hover:scale-125 transition-transform text-lg p-0.5"
                                          >{emoji}</button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </ScrollArea>
                    
                    {/* Message Input */}
                    <div className="p-4 border-t">
                      <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                        <Users className="h-3 w-3" />
                        <span>Shared with {circle?.name || 'your circle'}</span>
                      </div>
                      <div className="flex gap-2">
                        <MentionTextarea
                          value={messageText}
                          onChange={setMessageText}
                          members={circleMembers}
                          placeholder="Type a message to your sisters... Use @ to mention"
                          className="min-h-[40px] max-h-[100px] resize-none"
                          rows={1}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          dataTestId="circle-message-input"
                        />
                        <Button
                          onClick={sendMessage}
                          disabled={sending || !messageText.trim()}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          {sending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Check-ins Tab */}
                  <TabsContent value="checkins" className="m-0">
                    <ScrollArea className="h-[450px] p-4">
                      {/* Current Week's Prompt */}
                      {checkinPrompt && (
                        <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100 mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="h-4 w-4 text-purple-600" />
                            <span className="text-sm font-medium text-purple-700">
                              This Week's Reflection
                            </span>
                          </div>
                          <p className="text-forest italic">"{checkinPrompt.prompt}"</p>
                        </div>
                      )}

                      {checkins.length === 0 ? (
                        <div className="text-center py-12">
                          <CheckCircle className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-gray-500">No check-ins yet this week</p>
                          <p className="text-gray-400 text-sm">Be the first to share!</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {checkins.map(checkin => (
                            <div key={checkin.id} className="p-4 bg-white border rounded-lg">
                              <div className="flex items-start gap-3">
                                <Avatar className="h-10 w-10">
                                  <AvatarImage src={getProfileImageUrl(checkin.user?.profile_image)} />
                                  <AvatarFallback className="bg-purple-100 text-purple-600">
                                    {checkin.user?.full_name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium text-forest">
                                      {checkin.user?.full_name}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      {new Date(checkin.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  
                                  <p className="text-sm text-gray-700 mb-3">
                                    {checkin.response}
                                  </p>
                                  
                                  {checkin.wins?.length > 0 && (
                                    <div className="flex items-center gap-2 mb-2">
                                      <Trophy className="h-4 w-4 text-amber-500" />
                                      <span className="text-sm text-amber-700">
                                        Win: {checkin.wins[0]}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {checkin.goal_for_week && (
                                    <div className="flex items-center gap-2 mb-3">
                                      <Target className="h-4 w-4 text-green-600" />
                                      <span className="text-sm text-green-700">
                                        Goal: {checkin.goal_for_week}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {/* Reactions */}
                                  <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                                    {/* Reaction buttons */}
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-gray-500 mr-1">React:</span>
                                      {CELEBRATION_REACTIONS.slice(0, 5).map(reaction => (
                                        <button
                                          key={reaction}
                                          onClick={(e) => { e.stopPropagation(); reactToCheckin(checkin.id, reaction); }}
                                          className="hover:scale-125 transition-transform text-lg p-1 hover:bg-gray-100 rounded"
                                          title="Send encouragement!"
                                          data-testid={`react-btn-${checkin.id}-${reaction}`}
                                        >
                                          {reaction}
                                        </button>
                                      ))}
                                    </div>
                                    
                                    {/* Display existing reactions */}
                                    {checkin.reactions && checkin.reactions.length > 0 && (
                                      <div className="flex flex-wrap items-center gap-1">
                                        {/* Group reactions by emoji and show counts */}
                                        {Object.entries(
                                          checkin.reactions.reduce((acc, r) => {
                                            const emoji = r.reaction || '💎';
                                            acc[emoji] = (acc[emoji] || 0) + 1;
                                            return acc;
                                          }, {})
                                        ).map(([emoji, count]) => (
                                          <span 
                                            key={emoji} 
                                            className="inline-flex items-center bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5 text-sm"
                                            data-testid={`reaction-badge-${emoji}`}
                                          >
                                            {emoji} 
                                            {count > 1 && <span className="text-xs text-purple-600 ml-1 font-medium">{count}</span>}
                                          </span>
                                        ))}
                                        <span className="text-xs text-gray-400 ml-1">
                                          {checkin.reactions.length} {checkin.reactions.length === 1 ? 'reaction' : 'reactions'}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>
          </div>

          {/* Members Sidebar */}
          <div className="lg:col-span-1">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-forest">
                  Circle Sisters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {circle.members?.map((member, idx) => (
                    <div key={member.id || idx} className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={getProfileImageUrl(member.profile_image)} />
                        <AvatarFallback className="bg-purple-100 text-purple-600">
                          {member.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm text-forest">
                          {member.full_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {member.focus_area || 'General Support'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Focus Area Badge */}
                <div className="mt-6 p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Circle Focus</p>
                  <Badge className="bg-purple-600 text-white">
                    {circle.focus_area_label}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Weekly Check-In Dialog */}
        <Dialog open={showCheckinDialog} onOpenChange={setShowCheckinDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-purple-600" />
                Weekly Check-In
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* This Week's Prompt */}
              {checkinPrompt && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-sm text-purple-700 font-medium mb-1">This Week's Reflection:</p>
                  <p className="text-forest italic">"{checkinPrompt.prompt}"</p>
                </div>
              )}
              
              {/* Main Response */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-forest">
                  Your Response <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="Share your thoughts with your circle..."
                  value={checkinResponse}
                  onChange={(e) => setCheckinResponse(e.target.value)}
                  rows={4}
                  maxLength={1000}
                />
              </div>
              
              {/* Win */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-forest flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-amber-500" />
                  A Win to Celebrate (optional)
                </label>
                <Input
                  placeholder="What's one win from this week?"
                  value={checkinWins}
                  onChange={(e) => setCheckinWins(e.target.value)}
                  maxLength={200}
                />
              </div>
              
              {/* Goal */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-forest flex items-center gap-2">
                  <Target className="h-4 w-4 text-green-600" />
                  Goal for Next Week (optional)
                </label>
                <Input
                  placeholder="What are you focusing on next?"
                  value={checkinGoal}
                  onChange={(e) => setCheckinGoal(e.target.value)}
                  maxLength={200}
                />
              </div>
              
              {/* Points Info */}
              <div className="p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                  <Sparkles className="h-4 w-4" />
                  You'll earn 15 diamonds for completing your check-in!
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCheckinDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitCheckin}
                disabled={submittingCheckin || !checkinResponse.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {submittingCheckin ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
                ) : (
                  <><CheckCircle className="h-4 w-4 mr-2" />Submit Check-In</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CircleChatPage;
